import React, {
  FC, useCallback, useEffect, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { AddArticleRequestForm } from '../addArticleRequestForm';
import { EditDraftModalComponent } from './types';
import Modal from 'components/base/Modal';

import { OrderLine, OrderLineChannel } from 'config/apiFunus/generated/data-contracts';
import { useArticles } from 'hooks/useArticles';
import { OrderDraft } from 'models/OrderDraft';
import { showErrorToast, showSuccessToast } from 'utils/toasts';

export const EditDraftModal: FC<EditDraftModalComponent> = ({
  show,
  articleId,
  onSuccess,
  onHide,
  saveUpdate,
}) => {
  const {
    getArticleDraft, acceptDraft, fetchArticleProviders, articleProviders,
  } = useArticles();
  const { t } = useTranslation();
  const [articleDraft, setArticleDraft] = useState<OrderDraft>();
  const [payload, setpayload] = useState<OrderLine & {idSupplier?: string}>();
  const [selectedArticle, setselectedArticle] = useState<string>();
  const [acceptDraftLoading, setAcceptDraftLoading] = useState<boolean>(false);
  const [isFormReady, setIsFormReady] = useState<boolean>(false);

  useEffect(
    () => {
      if (selectedArticle) {
        fetchArticleProviders(selectedArticle)
          .catch((error) => {
            showErrorToast(error.message);
          });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedArticle],
  );

  useEffect(
    () => {
      if (articleId && show) {
        getArticleDraft(articleId)
          .then((response) => {
            setArticleDraft(response);
            return response;
          }).catch(() => false);
      } else if (!articleId) {
        setArticleDraft(undefined);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [articleId, show],
  );

  const acceptRequest = useCallback(
    async () => {
      if (payload && articleId) {
        const supplier = articleProviders.find((s) => s.code === payload.idSupplier);
        const requestBody = {
          amount: 1,
          articleDescription: payload.articleDescription,
          category: payload.category,
          channel: OrderLineChannel.PROCESSING,
          client: payload.client,
          code: payload.code,
          comments: payload.comments,
          date: payload?.date ? new Date(payload?.date) : undefined,
          dedicatory: payload.dedicatory,
          erpId: payload.erpId,
          idSupplier: supplier?.code || '',
          observations: payload.observations,
          price: payload.price,
          supplier: supplier?.email || '',
          time: payload.time,
        };
        if (saveUpdate) {
          setAcceptDraftLoading(true);
          acceptDraft(articleId, requestBody).then(() => {
            showSuccessToast(t('article.flowers.updateDraftSuccess'));
            onSuccess?.(articleId, requestBody);
            setAcceptDraftLoading(false);
            return {};
          }).catch((error) => {
            showErrorToast(error.message);
            setAcceptDraftLoading(false);
          });
        } else {
          onSuccess?.(articleId, requestBody);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [articleId, articleProviders, onSuccess, payload, t],
  );

  const onClose = useCallback(
    () => {
      setArticleDraft(undefined);
      onHide();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Modal
      buttons={[
        {
          color: 'secondary',
          onClick: () => {
            onClose();
          },
          text: t('common.cancel'),
        },
        {
          disabled: acceptDraftLoading || !isFormReady,
          onClick: acceptRequest,
          text: t('common.accept'),
        },
      ]}
      closeOnFocusLost={false}
      show={show}
      showCancel={false}
      title={t('article.flowers.editDraft')}
      onHide={() => {
        onClose();
      }}
    >
      {articleDraft
        ? (
          <AddArticleRequestForm
            channel={OrderLineChannel.PROCESSING}
            clientDisabled={false}
            erpId={articleDraft.erpId}
            id={articleDraft.recordId}
            priceDisabled={false}
            providerDisabled={false}
            value={{
              ...articleDraft,
              amount: 1,
              date: articleDraft.date ? new Date(articleDraft.date) : undefined,
              idSupplier: articleDraft.idSupplier,
              price: articleDraft.price,
              supplier: '',
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              time: articleDraft?.time as any,
            }}
            vigilDate={articleDraft.date}
            vigilHour={articleDraft.vigilAndCeremonyOutsideCabre ? '' : articleDraft.time}
            articleDisabled
            articleTypeDisabled
            showArticleDescription
            showArticleSelector
            showCategorySelector
            showClientSelector
            onArticleChange={() => false}
            onChange={(p) => {
              setselectedArticle(p.code);
              setpayload(p);
            }}
            onIsValidUpdate={(isValid) => {
              setIsFormReady(isValid);
            }}
            onSupplierChange={() => false}
          />
        )
        : <React.Fragment />}
    </Modal>
  );
};
